import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  Bot,
  Pencil,
  Plus,
  Shield,
  Trash2,
  UserPlus,
  Users as UsersIcon,
  X,
} from "lucide-react";
import { z } from "zod";
import {
  InboxVisibilitySchema,
  MembershipRoleSchema,
  PhoneE164Schema,
  UserStatusSchema,
  type Rooftop,
  type UpdateUserDto,
  type User,
  type UserMembership,
} from "@lumika/shared";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormField,
  Input,
  MobileList,
  MobileListRow,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  useZodForm,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";
import { formatDateTime } from "../../lib/format";

const InboxVisibilityLabels: Record<z.infer<typeof InboxVisibilitySchema>, string> = {
  assigned_only: "Assigned only",
  assigned_and_unassigned: "Assigned + unassigned",
  rooftop_all: "All in rooftop",
  group_all: "All in dealership",
};

// Sentinel Select value for a group-wide membership (rooftopId = null) —
// the user sees every rooftop in the dealership. The display side already
// renders a null rooftopId as "All rooftops".
const ALL_ROOFTOPS = "__all__";

// Sentinel Select value for "no department" — maps back to "" in the form.
const NO_DEPARTMENT = "__none__";

// Page-local department vocabulary. The server field (CreateUserSchema.
// department) is free text, so a stored value outside this list stays
// selectable below (same pattern as the rooftop timezone select).
// TODO: promote to a shared UserDepartmentSchema in @lumika/shared and
// adopt it server-side and in the dealer app's PeoplePage.
const USER_DEPARTMENTS = ["sales", "service", "parts", "bdc", "finance", "admin"] as const;

const DepartmentLabels: Record<(typeof USER_DEPARTMENTS)[number], string> = {
  sales: "Sales",
  service: "Service",
  parts: "Parts",
  bdc: "BDC",
  finance: "Finance",
  admin: "Admin",
};

const RoleLabels: Record<z.infer<typeof MembershipRoleSchema>, string> = {
  dealership_owner: "Owner",
  dealership_admin: "Admin",
  sales_manager: "Sales manager",
  service_manager: "Service manager",
  bdc_agent: "BDC agent",
  sales_agent: "Sales agent",
  service_agent: "Service agent",
  readonly: "Read-only",
};

const UserFormSchema = z.object({
  email: z.string().email("Invalid email.").or(z.literal("")).optional(),
  firstName: z.string().trim().max(80).optional(),
  lastName: z.string().trim().max(80).optional(),
  jobTitle: z.string().trim().max(80).optional(),
  department: z.string().trim().max(80).optional(),
  phone: z.union([z.literal(""), PhoneE164Schema]).optional(),
  status: UserStatusSchema,
  roundRobinEnabled: z.boolean(),
  // Role to grant on invite. Drives the membership created alongside the
  // user, which is what triggers the "Welcome to Lumika" email. Ignored in
  // edit mode (memberships are managed by the editor below).
  role: MembershipRoleSchema.optional(),
}).superRefine((values, ctx) => {
  // Human users must have an email. The AI user's email is system-managed
  // and always present, so an empty email only happens for human users.
  if (!values.email?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["email"],
      message: "Email is required for human users.",
    });
  }
});

type UserFormValues = z.infer<typeof UserFormSchema>;

const EMPTY_USER: UserFormValues = {
  email: "",
  firstName: "",
  lastName: "",
  jobTitle: "",
  department: "",
  phone: "",
  status: "pending",
  roundRobinEnabled: true,
  role: "bdc_agent",
};

function toEditValues(user: User): UserFormValues {
  return {
    email: user.email ?? "",
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    jobTitle: user.jobTitle ?? "",
    department: user.department ?? "",
    phone: user.phone ?? "",
    status: user.status,
    roundRobinEnabled: user.roundRobinEnabled,
  };
}

function toPayload(values: UserFormValues): UpdateUserDto {
  return {
    email: values.email?.trim() ? values.email.trim() : null,
    firstName: values.firstName?.trim() ? values.firstName.trim() : null,
    lastName: values.lastName?.trim() ? values.lastName.trim() : null,
    displayName: composeDisplayName(values.firstName, values.lastName),
    jobTitle: values.jobTitle?.trim() ? values.jobTitle.trim() : null,
    department: values.department?.trim() ? values.department.trim() : null,
    phone: values.phone?.trim() ? values.phone.trim() : null,
    status: values.status,
    roundRobinEnabled: values.roundRobinEnabled,
  };
}

function composeDisplayName(first?: string, last?: string): string | null {
  const composed = [first?.trim(), last?.trim()].filter(Boolean).join(" ");
  return composed || null;
}

function userDisplayName(user: User): string {
  if (user.displayName) return user.displayName;
  const composed = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return composed || user.email || "Unnamed";
}

export function DealershipUsersPage() {
  const { dealershipId } = useParams();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<User | "new" | null>(null);
  const [pendingDelete, setPendingDelete] = useState<User | null>(null);

  const detailQuery = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId!),
    enabled: Boolean(dealershipId),
  });
  const usersQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "users"],
    queryFn: () => adminApi.listUsers(dealershipId!),
    enabled: Boolean(dealershipId),
  });

  const users = usersQuery.data ?? [];
  const aiUsers = useMemo(() => users.filter((u) => u.kind === "ai"), [users]);
  const humanUsers = useMemo(() => users.filter((u) => u.kind !== "ai"), [users]);
  const memberships = detailQuery.data?.memberships ?? [];
  const rooftops = detailQuery.data?.rooftops ?? [];

  const membershipsByUser = useMemo(() => {
    const map = new Map<string, UserMembership[]>();
    for (const m of memberships) {
      const list = map.get(m.userId) ?? [];
      list.push(m);
      map.set(m.userId, list);
    }
    return map;
  }, [memberships]);

  const rooftopName = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rooftops) m.set(r.id, r.name);
    return m;
  }, [rooftops]);

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => adminApi.removeUser(dealershipId!, userId),
    onSuccess: async () => {
      setPendingDelete(null);
      await invalidateUserData(qc, dealershipId);
      toast.success("User removed");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        eyebrow="People"
        title="Team"
        description="Operators with access to this dealership. Memberships scope what each user sees and can do."
        actions={
          <Button onClick={() => setEditing("new")}>
            <UserPlus className="h-4 w-4" />
            Invite user
          </Button>
        }
      />
      <Section
        title="AI users"
        description="Platform-managed AI operators — one per dealership. Editable, but provisioned automatically and cannot be removed."
      >
        {usersQuery.isLoading ? (
          <PageLoading message="Loading users…" />
        ) : usersQuery.isError ? (
          <PageError
            title="Couldn't load users"
            description="Try refreshing the page."
          />
        ) : aiUsers.length === 0 ? (
          <EmptyState
            icon={<Bot className="h-5 w-5" />}
            title="No AI user"
            description="A dealership AI user is provisioned automatically."
          />
        ) : (
          <>
            <TableBleed className="-my-6 hidden overflow-hidden rounded-b-lg md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last login</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aiUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{userDisplayName(user)}</TableCell>
                      <TableCell className="text-muted-foreground">{user.email ?? "—"}</TableCell>
                      <TableCell>
                        <Badge variant={user.status === "active" ? "success" : user.status === "pending" ? "warning" : "secondary"}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateTime(user.lastLoginAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Edit AI user"
                          onClick={() => setEditing(user)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableBleed>
            <MobileList>
              {aiUsers.map((user) => (
                <MobileListRow
                  key={user.id}
                  primary={userDisplayName(user)}
                  secondary={user.email ?? "—"}
                  tertiary={`Last login ${formatDateTime(user.lastLoginAt)}`}
                  meta={
                    <Badge variant={user.status === "active" ? "success" : user.status === "pending" ? "warning" : "secondary"}>
                      {user.status}
                    </Badge>
                  }
                  trailing={
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Edit AI user"
                      onClick={() => setEditing(user)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  }
                />
              ))}
            </MobileList>
          </>
        )}
      </Section>

      <Section
        title={
          usersQuery.data
            ? `${humanUsers.length} ${humanUsers.length === 1 ? "user" : "users"}`
            : "Users"
        }
      >
        {usersQuery.isLoading ? (
          <PageLoading message="Loading users…" />
        ) : usersQuery.isError ? (
          <PageError
            title="Couldn't load users"
            description="Try refreshing the page."
          />
        ) : humanUsers.length === 0 ? (
          <EmptyState
            icon={<UsersIcon className="h-5 w-5" />}
            title="No users yet"
            description="Invite the first operator. They'll show up here with an editable access policy."
            action={
              <Button onClick={() => setEditing("new")}>
                <UserPlus className="h-4 w-4" />
                Invite user
              </Button>
            }
          />
        ) : (
          <>
            <TableBleed className="-my-6 hidden overflow-hidden rounded-b-lg md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Access</TableHead>
                    <TableHead>Last login</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {humanUsers.map((user) => {
                    const userMemberships = membershipsByUser.get(user.id) ?? [];
                    return (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{userDisplayName(user)}</TableCell>
                        <TableCell className="text-muted-foreground">{user.email ?? "—"}</TableCell>
                        <TableCell>
                          <Badge variant={user.status === "active" ? "success" : user.status === "pending" ? "warning" : "secondary"}>
                            {user.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {userMemberships.length === 0 ? (
                            <span className="text-xs text-muted-foreground">No memberships</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {userMemberships.map((m) => (
                                <Badge key={m.id} variant="outline" className="font-normal">
                                  {RoleLabels[m.role]} · {m.rooftopId ? (rooftopName.get(m.rooftopId) ?? "—") : "All rooftops"}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDateTime(user.lastLoginAt)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Edit user"
                            onClick={() => setEditing(user)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Remove user"
                            onClick={() => setPendingDelete(user)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableBleed>
            <MobileList>
              {humanUsers.map((user) => {
                const userMemberships = membershipsByUser.get(user.id) ?? [];
                return (
                  <MobileListRow
                    key={user.id}
                    primary={userDisplayName(user)}
                    secondary={user.email ?? "—"}
                    tertiary={`Last login ${formatDateTime(user.lastLoginAt)}`}
                    meta={
                      <>
                        <Badge variant={user.status === "active" ? "success" : user.status === "pending" ? "warning" : "secondary"}>
                          {user.status}
                        </Badge>
                        {userMemberships.length === 0 ? (
                          <span className="text-xs text-muted-foreground">No memberships</span>
                        ) : (
                          userMemberships.map((m) => (
                            <Badge key={m.id} variant="outline" className="font-normal">
                              {RoleLabels[m.role]} · {m.rooftopId ? (rooftopName.get(m.rooftopId) ?? "—") : "All rooftops"}
                            </Badge>
                          ))
                        )}
                      </>
                    }
                    trailing={
                      <div className="flex items-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Edit user"
                          onClick={() => setEditing(user)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Remove user"
                          onClick={() => setPendingDelete(user)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    }
                  />
                );
              })}
            </MobileList>
          </>
        )}
      </Section>

      {editing !== null && dealershipId && (
        <UserFormDialog
          mode={editing === "new" ? "create" : "edit"}
          user={editing === "new" ? null : editing}
          memberships={
            editing !== "new" ? (membershipsByUser.get(editing.id) ?? []) : []
          }
          rooftops={rooftops}
          dealershipId={dealershipId}
          onClose={() => setEditing(null)}
        />
      )}

      <Dialog open={Boolean(pendingDelete)} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove user</DialogTitle>
            <DialogDescription>
              {pendingDelete &&
                `“${userDisplayName(pendingDelete)}” will be removed from the dealership along with their memberships.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
            >
              {deleteMutation.isPending ? "Removing…" : "Remove user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UserFormDialog({
  mode,
  user,
  memberships,
  rooftops,
  dealershipId,
  onClose,
}: {
  mode: "create" | "edit";
  user: User | null;
  memberships: UserMembership[];
  rooftops: Rooftop[];
  dealershipId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const form = useZodForm(UserFormSchema, user ? toEditValues(user) : EMPTY_USER);

  const createMutation = useMutation({
    mutationFn: async (input: UserFormValues) => {
      const created = await adminApi.createUser(
        dealershipId,
        toPayload(input) as Parameters<typeof adminApi.createUser>[1],
      );
      // Grant the chosen role right away. Creating the membership is what
      // sends the "Welcome to Lumika" email, so the invitee is emailed
      // immediately rather than only once a role is added later.
      if (input.role) {
        await adminApi.createUserMembership(dealershipId, created.id, {
          role: input.role,
          inboxVisibility: "assigned_and_unassigned",
        });
      }
      return created;
    },
    onSuccess: async () => {
      await invalidateUserData(qc, dealershipId);
      toast.success("User invited");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: (input: UserFormValues) =>
      adminApi.updateUser(dealershipId, user!.id, toPayload(input)),
    onSuccess: async () => {
      await invalidateUserData(qc, dealershipId);
      toast.success("User updated");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function submit() {
    const result = form.validate();
    if (!result.ok) return;
    if (mode === "create") {
      createMutation.mutate(result.data);
    } else {
      updateMutation.mutate(result.data);
    }
  }

  const { values, errors, setField } = form;
  const isPending = createMutation.isPending || updateMutation.isPending;
  // The AI user's `ai+<id>@lumika.ai` system address is immutable.
  const isAiUser = mode === "edit" && user?.kind === "ai";
  // Once the admin has typed anything, block the implicit close paths
  // (outside click, Escape) so a stray click can't wipe the form.
  const isDirty =
    JSON.stringify(values) !== JSON.stringify(user ? toEditValues(user) : EMPTY_USER);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Invite user" : "Edit user"}</DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Add an operator to this dealership."
              : "Update profile and access policy."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 overflow-y-auto pr-1">
          <fieldset className="grid gap-3">
            <legend className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Profile
            </legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                className="sm:col-span-2"
                label="Email"
                required={!isAiUser}
                optional={isAiUser}
                error={errors.email}
              >
                <Input
                  type="email"
                  autoFocus={mode === "create"}
                  disabled={isAiUser}
                  value={values.email ?? ""}
                  onChange={(e) => setField("email", e.target.value)}
                />
              </FormField>
              <FormField label="First name" optional error={errors.firstName}>
                <Input
                  value={values.firstName ?? ""}
                  onChange={(e) => setField("firstName", e.target.value)}
                />
              </FormField>
              <FormField label="Last name" optional error={errors.lastName}>
                <Input
                  value={values.lastName ?? ""}
                  onChange={(e) => setField("lastName", e.target.value)}
                />
              </FormField>
              <FormField label="Job title" optional error={errors.jobTitle}>
                <Input
                  value={values.jobTitle ?? ""}
                  onChange={(e) => setField("jobTitle", e.target.value)}
                  placeholder="BDC Lead"
                />
              </FormField>
              <FormField label="Department" optional error={errors.department}>
                <Select
                  value={values.department?.trim() ? values.department : NO_DEPARTMENT}
                  onValueChange={(v) =>
                    setField("department", v === NO_DEPARTMENT ? "" : v)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_DEPARTMENT}>— None —</SelectItem>
                    {/* A stored free-text department outside the list stays
                        selectable so the trigger isn't blank and an unrelated
                        edit can't silently overwrite it. */}
                    {values.department?.trim() &&
                      !(USER_DEPARTMENTS as readonly string[]).includes(values.department) && (
                        <SelectItem value={values.department}>{values.department}</SelectItem>
                      )}
                    {USER_DEPARTMENTS.map((d) => (
                      <SelectItem key={d} value={d}>
                        {DepartmentLabels[d]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField
                label="Phone"
                optional
                error={errors.phone}
                description="E.164 format, e.g. +15551234567."
              >
                <Input
                  type="tel"
                  inputMode="tel"
                  value={values.phone ?? ""}
                  onChange={(e) => setField("phone", e.target.value)}
                  placeholder="+15551234567"
                />
              </FormField>
              <FormField label="Status" required error={errors.status}>
                <Select
                  value={values.status}
                  onValueChange={(v) => setField("status", v as UserFormValues["status"])}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UserStatusSchema.options.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              {mode === "create" && (
                <FormField
                  label="Role"
                  required
                  description="The user is invited with this role and emailed a welcome link."
                >
                  <Select
                    value={values.role ?? "bdc_agent"}
                    onValueChange={(v) =>
                      setField("role", v as z.infer<typeof MembershipRoleSchema>)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MembershipRoleSchema.options.map((role) => (
                        <SelectItem key={role} value={role}>
                          {RoleLabels[role]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              )}
              <FormField
                className="sm:col-span-2"
                label="Round-robin assignment"
                description="Include this user in automated assignment rotations."
              >
                <div className="flex items-center gap-3">
                  <Switch
                    checked={values.roundRobinEnabled}
                    onCheckedChange={(checked) => setField("roundRobinEnabled", checked)}
                  />
                  <span className="text-sm text-muted-foreground">
                    {values.roundRobinEnabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </FormField>
            </div>
          </fieldset>

          {mode === "edit" && user && (
            <MembershipsEditor
              dealershipId={dealershipId}
              userId={user.id}
              memberships={memberships}
              rooftops={rooftops}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {mode === "edit" ? "Close" : "Cancel"}
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending
              ? "Saving…"
              : mode === "create"
                ? "Create user"
                : "Save profile"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MembershipsEditor({
  dealershipId,
  userId,
  memberships,
  rooftops,
}: {
  dealershipId: string;
  userId: string;
  memberships: UserMembership[];
  rooftops: Rooftop[];
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  // Default to dealership-wide access — matches what the create-user
  // flow grants, and never produces an empty rooftopId (which would
  // fail uuid validation on a dealership with no rooftops yet).
  const [draft, setDraft] = useState<{
    role: z.infer<typeof MembershipRoleSchema>;
    rooftopId: string;
    inboxVisibility: z.infer<typeof InboxVisibilitySchema>;
  }>({
    role: "bdc_agent",
    rooftopId: ALL_ROOFTOPS,
    inboxVisibility: "assigned_and_unassigned",
  });

  const createMutation = useMutation({
    mutationFn: () =>
      adminApi.createUserMembership(dealershipId, userId, {
        role: draft.role,
        rooftopId: draft.rooftopId === ALL_ROOFTOPS ? null : draft.rooftopId,
        inboxVisibility: draft.inboxVisibility,
        permissions: {},
      }),
    onSuccess: async () => {
      await invalidateUserData(qc, dealershipId);
      setAdding(false);
      setDraft({
        role: "bdc_agent",
        rooftopId: ALL_ROOFTOPS,
        inboxVisibility: "assigned_and_unassigned",
      });
      toast.success("Membership added");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: {
        role?: z.infer<typeof MembershipRoleSchema>;
        rooftopId?: string | null;
        inboxVisibility?: z.infer<typeof InboxVisibilitySchema>;
      };
    }) => adminApi.updateUserMembership(dealershipId, id, patch),
    onSuccess: async () => {
      await invalidateUserData(qc, dealershipId);
      toast.success("Membership updated");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => adminApi.removeUserMembership(dealershipId, id),
    onSuccess: async () => {
      await invalidateUserData(qc, dealershipId);
      toast.success("Membership removed");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <fieldset className="grid gap-3">
      <legend className="flex w-full items-center justify-between text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
        Access
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-3 w-3" />
            Add membership
          </Button>
        )}
      </legend>

      {memberships.length === 0 && !adding && (
        <div className="rounded-md border border-dashed bg-muted/30 p-4 text-center text-sm text-muted-foreground">
          <Shield className="mx-auto mb-2 h-4 w-4" />
          No memberships yet. Add one to grant this user access to inbox and ops screens.
        </div>
      )}

      {memberships.map((m) => (
        <div
          key={m.id}
          className="grid grid-cols-1 items-end gap-3 rounded-md border bg-card p-3 sm:grid-cols-[1fr_1fr_1fr_auto]"
        >
          <FormField label="Role">
            <Select
              value={m.role}
              disabled={updateMutation.isPending}
              onValueChange={(v) =>
                updateMutation.mutate({
                  id: m.id,
                  patch: { role: v as z.infer<typeof MembershipRoleSchema> },
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MembershipRoleSchema.options.map((role) => (
                  <SelectItem key={role} value={role}>
                    {RoleLabels[role]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Rooftop">
            <Select
              value={m.rooftopId ?? ALL_ROOFTOPS}
              disabled={updateMutation.isPending}
              onValueChange={(v) =>
                updateMutation.mutate({
                  id: m.id,
                  patch: { rooftopId: v === ALL_ROOFTOPS ? null : v },
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_ROOFTOPS}>All rooftops</SelectItem>
                {rooftops.map((rooftop) => (
                  <SelectItem key={rooftop.id} value={rooftop.id}>
                    {rooftop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Inbox visibility">
            <Select
              value={m.inboxVisibility}
              disabled={updateMutation.isPending}
              onValueChange={(v) =>
                updateMutation.mutate({
                  id: m.id,
                  patch: { inboxVisibility: v as z.infer<typeof InboxVisibilitySchema> },
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {InboxVisibilitySchema.options.map((option) => (
                  <SelectItem key={option} value={option}>
                    {InboxVisibilityLabels[option]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Remove membership"
            onClick={() => {
              const rooftopLabel = m.rooftopId
                ? (rooftops.find((r) => r.id === m.rooftopId)?.name ?? "—")
                : "All rooftops";
              if (
                window.confirm(
                  `Remove the “${RoleLabels[m.role]} · ${rooftopLabel}” membership? The user loses that access immediately.`,
                )
              ) {
                removeMutation.mutate(m.id);
              }
            }}
            disabled={removeMutation.isPending}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      {adding && (
        <div className="grid grid-cols-1 items-end gap-3 rounded-md border bg-card p-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
          <FormField label="Role">
            <Select
              value={draft.role}
              onValueChange={(v) =>
                setDraft((d) => ({ ...d, role: v as z.infer<typeof MembershipRoleSchema> }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MembershipRoleSchema.options.map((role) => (
                  <SelectItem key={role} value={role}>
                    {RoleLabels[role]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Rooftop">
            <Select
              value={draft.rooftopId}
              onValueChange={(v) => setDraft((d) => ({ ...d, rooftopId: v }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_ROOFTOPS}>All rooftops</SelectItem>
                {rooftops.map((rooftop) => (
                  <SelectItem key={rooftop.id} value={rooftop.id}>
                    {rooftop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Inbox visibility">
            <Select
              value={draft.inboxVisibility}
              onValueChange={(v) =>
                setDraft((d) => ({
                  ...d,
                  inboxVisibility: v as z.infer<typeof InboxVisibilitySchema>,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {InboxVisibilitySchema.options.map((option) => (
                  <SelectItem key={option} value={option}>
                    {InboxVisibilityLabels[option]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              aria-label="Cancel"
              onClick={() => setAdding(false)}
            >
              <X className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? "Adding…" : "Add"}
            </Button>
          </div>
        </div>
      )}
    </fieldset>
  );
}

async function invalidateUserData(
  qc: ReturnType<typeof useQueryClient>,
  dealershipId?: string,
) {
  if (!dealershipId) return;
  await Promise.all([
    qc.invalidateQueries({ queryKey: ["admin-bdc", dealershipId, "users"] }),
    qc.invalidateQueries({ queryKey: ["dealership", dealershipId] }),
  ]);
}

